import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../../components/PaginationBar';
import { statusBadgeCx } from '../../utils/statusBadges';
import { fetchOrders, FetchOrdersParams, OrderResponse } from '../../api/orders';
import { fetchCurrentUser, type MeResponse } from '../../api/auth';

const PAGE_SIZE = 10;

const PendingOrdersPage: FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchCurrentUser()
      .then((resp) => { if (mounted) setMe(resp); })
      .catch(() => { if (mounted) setMe(null); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const params: FetchOrdersParams = { page, limit: PAGE_SIZE, status: 'draft' } as any;
      const role = (me?.role ?? me?.user?.typeKey ?? '') as string;
      const userLocs = (me?.user as any)?.locations as Array<{ id: number; name: string }> | undefined;
      if ((role || '').toUpperCase() === 'ADVISOR' || (role || '').toUpperCase() === 'STORE_MANAGER') {
        if (Array.isArray(userLocs) && userLocs.length > 0) {
          params.locationErpIds = userLocs.map((l) => l.id);
        }
      }
      try {
        const resp = await fetchOrders(params);
        if (ignore) return;
        setOrders(resp.data);
        setTotalPages(resp.meta.totalPages);
        setTotal(resp.meta.total);
      } catch (e) {
        if (!ignore) setError(e instanceof Error ? e.message : 'Failed to load pending orders');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void load();
    return () => { ignore = true; };
  }, [page]);

  const summary = useMemo(() => {
    if (!total) return 'No pending orders';
    const start = (page - 1) * PAGE_SIZE + (orders.length ? 1 : 0);
    const end = (page - 1) * PAGE_SIZE + orders.length;
    return `Showing ${start}-${end} of ${total} pending orders`;
  }, [orders.length, page, total]);

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">Pending Orders</h1>
          <p className="max-w-2xl text-sm text-slate-500">Review draft orders for manager approval.</p>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Order #</th>
              <th className="px-5 py-3 font-semibold">Customer</th>
              <th className="px-5 py-3 font-semibold">Vehicle</th>
              <th className="px-5 py-3 font-semibold">Location</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Created</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={7}>Loading…</td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={7}>{error}</td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={7}>No draft orders.</td>
              </tr>
            ) : (
              orders.map((order) => {
                const customerName = order.customer
                  ? `${order.customer.firstName ?? ''} ${order.customer.lastName ?? ''}`.trim() || order.customer.email || order.customer.mobileNumber || 'Customer'
                  : 'Customer';
                const vehicleLabel = order.customerVehicle
                  ? [order.customerVehicle.vehicleBrand?.name, order.customerVehicle.vehicleModel?.name, order.customerVehicle.vehicleRegistrationNumber]
                    .filter(Boolean)
                    .join(' · ')
                  : '—';
                return (
                  <tr key={order.id} className="even:bg-slate-50/70">
                    <td className="px-5 py-3 font-semibold text-slate-700">{order.orderNumber}</td>
                    <td className="px-5 py-3 text-slate-600">{customerName}</td>
                    <td className="px-5 py-3 text-slate-600">{vehicleLabel}</td>
                    <td className="px-5 py-3 text-slate-600">{((order as any).location?.name as string) || order.locationCode || '—'}</td>
                    <td className="px-5 py-3"><span className={statusBadgeCx(order.status)}>{order.status}</span></td>
                    <td className="px-5 py-3 text-slate-500">{new Date(order.createdAt).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                          onClick={() => navigate(`/order-management/manager-approval/${order.id}`)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                          onClick={() => navigate(`/order-management/${order.id}`, {
                            state: { from: '/order-management/pending' }
                          })}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <PaginationBar
          page={page}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          onSelect={(v) => setPage(v)}
          disabled={loading || totalPages === 0}
          renderSummary={<span>{summary}</span>}
        />
      </div>
    </div>
  );
};

export default PendingOrdersPage;
